import { apiClient } from '@/api/client';
import type {
  ApplicationSource,
  Gender,
  GuardianRelationship,
  PaginatedStudents,
  StudentResponse,
  TransferStudentBranchRequest,
} from '@/types/shared';

export interface ListStudentsParams {
  page?: number;
  pageSize?: number;
  /** `field:ASC` / `field:DESC` — allowed fields per students.service.ts `applySort`:
   * `studentCode`, `nameAr`, `nameEn`, `applicationDate`, `createdAt`. */
  sort?: string;
  search?: string;
  grade?: number;
  agreedSchool?: number;
  gender?: Gender;
  /** Admin-only server-side (silently ignored for other roles) — students.service.ts `list()`. */
  includeDeleted?: boolean;
  /** T175 (§20, openapi `BranchFilter`). Narrows WITHIN the caller's resolved branch scope only —
   * never widens it. Omit entirely (never send `undefined`) for a single-branch caller — the UI
   * hides this filter for them (T159 §5.2), and the server treats an absent value as "every branch
   * in scope" regardless. */
  branch?: number;
}

/** GET /students — readable by all staff roles. */
export async function listStudents(params: ListStudentsParams): Promise<PaginatedStudents> {
  const { data } = await apiClient.get<PaginatedStudents>('/students', { params });
  return data;
}

/** GET /students/:studentCode — includes `linkedRecords`. 404s for soft-deleted students (the
 * service's `findByCode` does not pass `withDeleted`). */
export async function getStudent(studentCode: string): Promise<StudentResponse> {
  const { data } = await apiClient.get<StudentResponse>(
    `/students/${encodeURIComponent(studentCode)}`,
  );
  return data;
}

/** Mirrors `RegisterStudentDto` (backend-nodejs, frozen). The server issues the immutable Student
 * Code — never send/collect it here. */
export interface RegisterStudentPayload {
  nameAr: string;
  nameEn: string;
  dateOfBirth: string;
  gender: Gender;
  nationalId?: string;
  gradeId: number;
  agreedSchoolId: number;
  educationalAdministrationId: number;
  address?: string;
  guardianNameAr?: string;
  guardianNameEn?: string;
  guardianRelationship: GuardianRelationship;
  guardianPhone: string;
  guardianSecondaryPhone?: string;
  guardianEmail?: string;
  guardianNationalId?: string;
  previousSchool?: string;
  applicationSource?: ApplicationSource;
  admissionNotes?: string;
  installmentCount?: number;
  totalAgreedFees?: number;
  /** T152 (`docs/architecture/16-referral-partners.md` §7, endpoints 8/9). OPTIONAL, at most one.
   * On CREATE: omit or send an id — never `null` (there is nothing to clear yet). On EDIT: the
   * three wire states are NOT interchangeable — `undefined`/absent (key not sent) leaves the
   * student's link untouched, a number links/changes it, and explicit `null` CLEARS it and removes
   * the recognized referral cost (422 `LOOKUP_VALUE_INACTIVE` if the id is inactive/unknown). See
   * `StudentFormPage`'s `referralPartnerTouched` tracking for how the three states are produced. */
  referralPartnerId?: number | null;
  /** T175 (§20, DEC-BR10). CREATE-ONLY — omit for a single-branch caller (the server defaults it
   * from their scope, FR-18); required when the caller is multi/all-branches (else 422
   * `BRANCH_REQUIRED`); an id outside the caller's own scope is 422 `BRANCH_OUT_OF_SCOPE` (FR-19).
   * Deliberately EXCLUDED from `UpdateStudentPayload` below (DEC-BRA6) — `branchId` is NOT a field
   * on `PATCH /students/{studentCode}`; the only way to change an existing student's branch is the
   * dedicated `transferStudentBranch()` below. */
  branchId?: number;
}

/** `UpdateStudentDto` — same shape (minus `branchId`, see its doc comment above), all optional,
 * `studentCode` never included (server rejects it with `STUDENT_CODE_IMMUTABLE` if present in the
 * body at all). */
export type UpdateStudentPayload = Partial<Omit<RegisterStudentPayload, 'branchId'>>;

export interface FieldWarning {
  field: string;
  message: string;
}

export interface RegisterStudentResult {
  student: StudentResponse;
  /** Non-blocking national-ID checksum/consistency warnings the server computed, carried on the
   * `X-Warnings` response header (students.controller.ts `register()`) — not part of the JSON
   * body. Best-effort parse; absent/malformed header just yields an empty array. */
  warnings: FieldWarning[];
}

function parseWarningsHeader(headerValue: unknown): FieldWarning[] {
  if (typeof headerValue !== 'string' || !headerValue) return [];
  try {
    const parsed = JSON.parse(headerValue);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item): item is FieldWarning =>
          item && typeof item === 'object' && typeof item.message === 'string',
      );
    }
  } catch {
    // Malformed header — ignore rather than fail the whole registration flow over it.
  }
  return [];
}

/** POST /students — Roles: Admin, Admissions Officer. */
export async function registerStudent(
  payload: RegisterStudentPayload,
): Promise<RegisterStudentResult> {
  const response = await apiClient.post<StudentResponse>('/students', payload);
  return {
    student: response.data,
    warnings: parseWarningsHeader(response.headers['x-warnings']),
  };
}

/** PATCH /students/:studentCode — Roles: Admin, Admissions Officer. Partial update. */
export async function updateStudent(
  studentCode: string,
  payload: UpdateStudentPayload,
): Promise<StudentResponse> {
  const { data } = await apiClient.patch<StudentResponse>(
    `/students/${encodeURIComponent(studentCode)}`,
    payload,
  );
  return data;
}

/** DELETE /students/:studentCode — Admin only, soft-delete. */
export async function deleteStudent(studentCode: string): Promise<void> {
  await apiClient.delete(`/students/${encodeURIComponent(studentCode)}`);
}

/** POST /students/:studentCode/transfer-branch — Admin only (T165/T175, §20 DEC-BR17). The ONLY
 * way to change an existing student's branch (`branchId` is not a field on the PATCH above,
 * DEC-BRA6). Writes `students.branch_id` and one append-only `student_branch_transfers` row —
 * deliberately does NOT touch any already-recognized revenue-bearing row (`payment_plans`,
 * `student_down_payments`, `exam_reservation_snapshots`); those keep reporting against the branch
 * that was in effect when each row was created (FR-22/FR-42). Returns the rebuilt student so the
 * caller can swap it straight into state without a second round-trip. */
export async function transferStudentBranch(
  studentCode: string,
  payload: TransferStudentBranchRequest,
): Promise<StudentResponse> {
  const { data } = await apiClient.post<StudentResponse>(
    `/students/${encodeURIComponent(studentCode)}/transfer-branch`,
    payload,
  );
  return data;
}

/** POST /students/:studentCode/restore — Admin only. */
export async function restoreStudent(studentCode: string): Promise<StudentResponse> {
  const { data } = await apiClient.post<StudentResponse>(
    `/students/${encodeURIComponent(studentCode)}/restore`,
  );
  return data;
}
